import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import classNames from 'classnames';

import Heading from 'components/partials/heading/heading';
import Search from 'components/partials/search/search';
import Button from 'components/partials/button/button';

import Icons from 'assets/icons';

interface ActionButtonConfig {
  to: string;
  label: string;
  testId?: string;
  icon?: React.ReactNode;
}

/**
 * `inline` keeps the search box inside the title row, to the left of the action
 * button. `full` moves it to its own full-width row underneath.
 */
export type TabHeaderSearchVariant = 'inline' | 'full';

interface TabHeaderProps {
  title: string;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchVariant?: TabHeaderSearchVariant;
  actionButton?: ActionButtonConfig | null;
  filters?: React.ReactNode;
  customClass?: string;
  headingVariant?: 'h1' | 'h2' | 'h3';
}

const TabHeader = ({
  title,
  search: _search,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchVariant = 'inline',
  actionButton,
  filters,
  customClass,
  headingVariant = 'h2',
}: TabHeaderProps): React.JSX.Element => (
  <div className={classNames('mb-4', customClass)}>
    <div className="flex items-center justify-between gap-4 my-4">
      <Heading variant={headingVariant} customClass="shrink-0">
        {title}
      </Heading>

      <div className="flex items-center gap-3">
        {onSearchChange && searchVariant === 'inline' && (
          <div className="w-96" data-testid="tab-header-search-inline">
            <Search
              setSearch={onSearchChange}
              placeholder={searchPlaceholder}
              customClass="!max-h-11"
            />
          </div>
        )}

        {actionButton && (
          <RouterLink to={actionButton.to} data-testid={actionButton.testId}>
            <Button
              variant="brand-filled"
              size="md"
              iconBefore={
                actionButton.icon || (
                  <Icons.Plus aria-label="PlusIcon" height="100%" width="14" />
                )
              }
            >
              {actionButton.label}
            </Button>
          </RouterLink>
        )}
      </div>
    </div>

    {onSearchChange && searchVariant === 'full' && (
      <div data-testid="tab-header-search-full">
        <Search
          setSearch={onSearchChange}
          placeholder={searchPlaceholder}
          customClass="w-full mb-4"
        />
      </div>
    )}

    {filters && <div className="relative">{filters}</div>}
  </div>
);

export default TabHeader;
